import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Avatar } from '@/features/auth/Avatar'
import type { UserCard } from '@/features/auth/types'
import { createGame } from '@/shared/game/api'
import { acceptFriend, addFriend, inviteToGame, removeFriend, searchUsers } from './api'
import { friendsKey, requestsKey, useFriendRequests, useFriends } from './useFriends'

type Props = {
  onOpenGame: (gameId: string) => void
  onOpenUser: (userId: number) => void
}

/**
 * Друзья: кто в сети, кто зовёт, кого позвать.
 *
 * Кнопка «позвать играть» здесь создаёт партию сама, а не ведёт в лобби:
 * иначе позвать друга — это три шага (создай, скопируй ссылку, отправь),
 * а смысл списка друзей ровно в том, чтобы шаг был один.
 */
export function FriendsScreen({ onOpenGame, onOpenUser }: Props) {
  const client = useQueryClient()
  const friends = useFriends(true)
  const requests = useFriendRequests(true)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<UserCard[] | null>(null)

  const refresh = () => {
    void client.invalidateQueries({ queryKey: friendsKey })
    void client.invalidateQueries({ queryKey: requestsKey })
  }

  const search = useMutation({
    mutationFn: () => searchUsers(query.trim()),
    onSuccess: setFound,
  })

  const invite = useMutation({
    mutationFn: (nickname: string) => addFriend(nickname),
    onSuccess: () => {
      refresh()
      if (query.trim() !== '') search.mutate()
    },
  })

  const accept = useMutation({ mutationFn: acceptFriend, onSuccess: refresh })
  const remove = useMutation({ mutationFn: removeFriend, onSuccess: refresh })

  const callToGame = useMutation({
    mutationFn: async (userId: number) => {
      const game = await createGame()
      await inviteToGame(game.id, userId)

      return game.id
    },
    onSuccess: onOpenGame,
  })

  return (
    <div className="lobby">
      <div className="hero">
        <h2 className="hero__title">Друзья</h2>
        <p className="hero__lead">
          Позовите друга — и партия соберётся без пересылки ссылок. Приглашение придёт ему сразу, а
          если он не в сети, дождётся его на главной.
        </p>
      </div>

      <div className="card">
        <h2 className="card__title">Найти по нику</h2>
        <form
          className="field"
          onSubmit={(event) => {
            event.preventDefault()
            if (query.trim().length >= 2) search.mutate()
          }}
        >
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Логин"
            maxLength={32}
          />
          <button className="primary" type="submit" disabled={search.isPending}>
            Искать
          </button>
        </form>

        {found !== null && found.length === 0 && <p className="empty">никого с таким ником нет</p>}

        {found !== null && found.length > 0 && (
          <ul className="people">
            {found.map((user) => (
              <li key={user.id} className="person">
                <Person user={user} onOpen={onOpenUser} />
                {user.relation === 'none' && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => invite.mutate(user.nickname ?? '')}
                  >
                    позвать в друзья
                  </button>
                )}
                {user.relation === 'outgoing' && <span className="muted">заявка отправлена</span>}
                {user.relation === 'incoming' && (
                  <button type="button" className="ghost" onClick={() => accept.mutate(user.id)}>
                    принять
                  </button>
                )}
                {user.relation === 'friends' && <span className="muted">уже в друзьях</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {(requests.data?.incoming.length ?? 0) > 0 && (
        <div className="card">
          <h2 className="card__title">Вас зовут в друзья</h2>
          <ul className="people">
            {requests.data?.incoming.map((user) => (
              <li key={user.id} className="person">
                <Person user={user} onOpen={onOpenUser} />
                <button type="button" className="primary" onClick={() => accept.mutate(user.id)}>
                  принять
                </button>
                <button type="button" className="ghost" onClick={() => remove.mutate(user.id)}>
                  отказать
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(requests.data?.outgoing.length ?? 0) > 0 && (
        <div className="card">
          <h2 className="card__title">Ждут ответа</h2>
          <ul className="people">
            {requests.data?.outgoing.map((user) => (
              <li key={user.id} className="person">
                <Person user={user} onOpen={onOpenUser} />
                <button type="button" className="ghost" onClick={() => remove.mutate(user.id)}>
                  отозвать
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h2 className="card__title">Ваши друзья</h2>

        {(friends.data?.length ?? 0) === 0 ? (
          <p className="empty">пока никого</p>
        ) : (
          <ul className="people">
            {friends.data?.map((friend) => (
              <li key={friend.id} className="person">
                <Person user={friend} onOpen={onOpenUser} />

                {friend.gameId === null ? (
                  <button
                    type="button"
                    className="primary"
                    disabled={callToGame.isPending}
                    onClick={() => callToGame.mutate(friend.id)}
                  >
                    позвать играть
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => onOpenGame(friend.gameId ?? '')}
                  >
                    смотреть партию
                  </button>
                )}

                <button type="button" className="ghost" onClick={() => remove.mutate(friend.id)}>
                  убрать
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** Человек в списке: аватарка, ник и отметка «в сети». */
function Person({ user, onOpen }: { user: UserCard; onOpen: (userId: number) => void }) {
  const name = user.nickname ?? user.name

  return (
    <button type="button" className="person__who" onClick={() => onOpen(user.id)}>
      <Avatar name={name} src={user.avatar} online={user.online} />
      <span className="person__name">
        <span className="player__name">{name}</span>
        <span className="muted">
          {user.gameId !== null ? 'играет' : user.online ? 'в сети' : 'не в сети'}
        </span>
      </span>
    </button>
  )
}
